import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import useRealTimeMarketData from '../../../hooks/useRealTimeMarketData';
import { formatPrice, formatNumber, formatPercentage } from '../../../services/marketDataService';

const MarketDataGrid = ({ activeMarket = 'crypto' }) => {
  const [sortBy, setSortBy] = useState('marketCap');
  const [sortOrder, setSortOrder] = useState('desc');

  const {
    marketData,
    connectionStatus,
    dataQuality,
    lastUpdate,
    isLoading,
    error,
    refresh
  } = useRealTimeMarketData(activeMarket);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!marketData?.length) return [];
    
    return [...marketData]?.sort((a, b) => {
      const aVal = a?.[sortBy] || 0;
      const bVal = b?.[sortBy] || 0;
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [marketData, sortBy, sortOrder]);

  const MiniSparkline = ({ data, trend }) => {
    if (!data || data?.length === 0) {
      return <div className="w-16 h-8 bg-muted/20 rounded"></div>;
    }

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    return (
      <div className="w-16 h-8 flex items-end space-x-0.5">
        {data?.map((value, index) => (
          <div
            key={index}
            className={`flex-1 rounded-sm ${trend === 'up' ? 'bg-success/60' : 'bg-error/60'}`}
            style={{ height: `${((value - min) / range) * 100}%` }}
          />
        ))}
      </div>
    );
  };

  const ConnectionStatus = () => {
    const statusConfig = {
      connected: { color: 'text-success', icon: 'CheckCircle', label: 'Live Data' },
      connecting: { color: 'text-warning', icon: 'Loader2', label: 'Connecting' },
      disconnected: { color: 'text-muted-foreground', icon: 'WifiOff', label: 'Offline' },
      error: { color: 'text-error', icon: 'AlertCircle', label: 'Connection Error' }
    };

    const config = statusConfig?.[connectionStatus] || statusConfig?.disconnected;
    const qualityColor = dataQuality === 'good' ? 'text-success' : 
                        dataQuality === 'degraded' ? 'text-warning' : 'text-error';

    return (
      <div className="flex items-center space-x-2">
        <Icon 
          name={config?.icon} 
          size={12} 
          className={`${config?.color} ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`} 
        />
        <span className={`text-xs ${config?.color}`}>{config?.label}</span>
        {dataQuality !== 'good' && (
          <span className={`text-xs ${qualityColor}`}>
            ({dataQuality})
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
              <Icon name="BarChart3" size={20} className="text-secondary" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h2 className="text-xl font-semibold text-card-foreground">Market Overview</h2>
                <ConnectionStatus />
              </div>
              <p className="text-sm text-muted-foreground">
                {activeMarket === 'crypto' ? 'Real-time cryptocurrency data' : 'Live stock market data'}
                {lastUpdate && ` • Updated ${lastUpdate?.toLocaleTimeString()}`}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={refresh}
              disabled={isLoading}
            >
              <Icon name={isLoading ? "Loader2" : "RefreshCw"} size={16} className={isLoading ? "animate-spin" : ""} />
              Refresh
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="Filter" size={16} />
              Filter
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/market-analysis'}>
              View Details
            </Button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-md">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-error" />
              <span className="text-sm text-error">{error}</span>
            </div>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Asset</th>
              <th 
                className="text-right p-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-standard"
                onClick={() => handleSort('price')}
              >
                <div className="flex items-center justify-end space-x-1">
                  <span>Price</span>
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th 
                className="text-right p-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-standard"
                onClick={() => handleSort('change24h')}
              >
                <div className="flex items-center justify-end space-x-1">
                  <span>24h Change</span>
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th 
                className="text-right p-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-standard"
                onClick={() => handleSort('volume24h')}
              >
                <div className="flex items-center justify-end space-x-1">
                  <span>Volume</span>
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th 
                className="text-right p-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-standard"
                onClick={() => handleSort('marketCap')}
              >
                <div className="flex items-center justify-end space-x-1">
                  <span>Market Cap</span>
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">24h Chart</th>
              <th className="text-center p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedData?.length > 0 ? (
              sortedData?.map((asset) => (
                <tr key={asset?.id || asset?.symbol} className="border-b border-border hover:bg-muted/20 transition-standard">
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-accent">{asset?.symbol}</span>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-card-foreground">{asset?.name}</p>
                          {asset?.source && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              asset?.source === 'binance' ? 'bg-success/10 text-success' :
                              asset?.source === 'simulated'? 'bg-primary/10 text-primary' : 'bg-muted/30 text-muted-foreground'
                            }`}>
                              {asset?.source}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{asset?.symbol}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <p className="font-medium text-card-foreground">{formatPrice(asset?.price)}</p>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <Icon 
                        name={asset?.change24h > 0 ? 'ArrowUp' : 'ArrowDown'} 
                        size={12} 
                        className={asset?.change24h > 0 ? 'text-success' : 'text-error'} 
                      />
                      <span className={`font-medium ${asset?.change24h > 0 ? 'text-success' : 'text-error'}`}>
                        {formatPercentage(asset?.change24h)}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <p className="font-medium text-card-foreground">{formatNumber(asset?.volume24h)}</p>
                  </td>
                  <td className="p-4 text-right">
                    <p className="font-medium text-card-foreground">{formatNumber(asset?.marketCap)}</p>
                  </td>
                  <td className="p-4 text-right">
                    <MiniSparkline data={asset?.sparkline} trend={asset?.trend} />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center space-x-2">
                      <Button variant="outline" size="xs">
                        <Icon name="Plus" size={12} />
                        Add
                      </Button>
                      <Button variant="ghost" size="xs">
                        <Icon name="Bell" size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="p-8 text-center">
                  <div className="flex flex-col items-center space-y-3">
                    <Icon name={isLoading ? "Loader2" : "WifiOff"} size={32} className={`text-muted-foreground ${isLoading ? 'animate-spin' : ''}`} />
                    <p className="text-muted-foreground">
                      {isLoading ? 'Loading market data...' : 'No market data available'}
                    </p>
                    {!isLoading && (
                      <Button variant="outline" size="sm" onClick={refresh}>
                        <Icon name="RefreshCw" size={16} />
                        Try Again
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full"></div>
              <span className="text-muted-foreground">
                {activeMarket === 'crypto' ? 'Crypto Market Cap: $1.89T' : 'US Market Cap: $45.6T'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span className="text-muted-foreground">
                24h Volume: {activeMarket === 'crypto' ? '$89.5B' : '$156B'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                dataQuality === 'good' ? 'bg-success' : 
                dataQuality === 'degraded' ? 'bg-warning' : 'bg-error'
              }`}></div>
              <span className="text-muted-foreground">
                Data Quality: {dataQuality}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-muted-foreground">
              {lastUpdate ? `Updated: ${lastUpdate?.toLocaleTimeString()}` : 'Never updated'}
            </span>
            <ConnectionStatus />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketDataGrid;